import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { PageSEO } from '../components/SEO';
import { 
  User, 
  Building, 
  Wallet, 
  CreditCard, 
  Globe, 
  CheckCircle, 
  ArrowRight, 
  ArrowLeft,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Success Screen Component - defined outside main component
const OnboardingSuccessScreen = ({ onNavigateHome, onNavigateContact }) => (
  <div className="min-h-screen pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
    <PageSEO 
      title="Anfrage erfolgreich gesendet | Firmbay"
      description="Ihre Käufer-Anfrage wurde erfolgreich übermittelt."
      path="/onboarding"
      robots="noindex, nofollow"
    />
    <div className="max-w-2xl mx-auto">
      <Card className="border-2 border-green-200 shadow-xl">
        <CardContent className="pt-12 pb-10 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Vielen Dank für deine Anfrage!
          </h1>
          
          <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto">
            Wir haben deine Daten zur GmbH-Gründung erfolgreich erhalten und werden uns in der Regel innerhalb von 24 Stunden bei dir melden.
          </p>

          <div className="bg-blue-50 rounded-xl p-6 mb-8 text-left">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600" />
              Wie geht es weiter?
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="font-bold text-blue-600 mt-0.5">1.</span>
                <span>Wir prüfen die bereitgestellten Angaben auf Plausibilität und bereiten alle Unterlagen vor.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-blue-600 mt-0.5">2.</span>
                <span>Du erhältst eine E-Mail mit den nächsten Schritten und Terminvorschlägen.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-blue-600 mt-0.5">3.</span>
                <span>Wir begleiten dich durch den gesamten Gründungsprozess bis zur Eintragung.</span>
              </li>
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              size="lg"
              onClick={onNavigateHome}
              className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700"
              data-testid="success-home-btn"
            >
              Zur Startseite
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              onClick={onNavigateContact}
              data-testid="success-contact-btn"
            >
              Fragen? Kontaktiere uns
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
);

const OnboardingForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    // Abschnitt A - Basisdaten
    full_name: '',
    email: '',
    phone: '',
    address: '',
    date_of_birth: '',
    nationality: '',
    residence_germany: true,
    
    // Abschnitt B - Unternehmensdaten
    company_name: '',
    alternative_company_name: '',
    company_location: '',
    business_purpose: '',
    number_of_shareholders: 1,
    managing_directors: '',
    
    // Abschnitt C - Gründung & Kapital
    capital_type: 'own',
    capital_amount: 25000,
    ownership_structure: '',
    founding_timeline: 'immediate',
    
    // Abschnitt D - Bank & Organisation
    existing_bank_contact: false,
    assisted_account_opening: false,
    preferred_bank: '',
    
    // Abschnitt E - Online & Technik
    needs_landing_page: false,
    social_media: [],
    ai_bot_goals: [],
    
    // Abschnitt F - Bestätigung
    legal_confirmation: false,
  });

  const steps = [
    { number: 1, title: 'Basisdaten', icon: User },
    { number: 2, title: 'Unternehmen', icon: Building },
    { number: 3, title: 'Kapital', icon: Wallet },
    { number: 4, title: 'Bank', icon: CreditCard },
    { number: 5, title: 'Online', icon: Globe },
    { number: 6, title: 'Bestätigung', icon: CheckCircle },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleArrayChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: prev[name].includes(value)
        ? prev[name].filter(item => item !== value)
        : [...prev[name], value]
    }));
  };

  const handleNext = () => {
    setCurrentStep(prev => Math.min(prev + 1, 6));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.legal_confirmation) {
      toast({
        variant: "destructive",
        title: "Bestätigung erforderlich",
        description: "Bitte bestätige die rechtlichen Hinweise.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/onboarding`, formData);
      
      if (response.status === 201) {
        setIsSuccess(true);
      }
    } catch (error) {
      console.error('Error submitting onboarding form:', error);
      toast({
        variant: "destructive",
        title: "Fehler beim Senden",
        description: error.response?.data?.detail || "Bitte versuche es später erneut.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show success screen if form was submitted successfully
  if (isSuccess) {
    return (
      <OnboardingSuccessScreen 
        onNavigateHome={() => navigate('/')} 
        onNavigateContact={() => navigate('/kontakt')} 
      />
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Abschnitt A – Basisdaten</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vollständiger Name *
              </label>
              <input
                type="text"
                name="full_name"
                required
                value={formData.full_name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Max Mustermann"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-Mail-Adresse *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="max@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefonnummer *
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="+49 123 456789"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Aktuelle Anschrift *
              </label>
              <input
                type="text"
                name="address"
                required
                value={formData.address}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Musterstraße 123, 10115 Berlin"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Geburtsdatum *
                </label>
                <input
                  type="date"
                  name="date_of_birth"
                  required
                  value={formData.date_of_birth}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Staatsangehörigkeit *
                </label>
                <input
                  type="text"
                  name="nationality"
                  required
                  value={formData.nationality}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Deutsch"
                />
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Checkbox
                id="residence_germany"
                checked={formData.residence_germany}
                onCheckedChange={(checked) => 
                  setFormData(prev => ({ ...prev, residence_germany: checked }))
                }
              />
              <label htmlFor="residence_germany" className="text-sm text-gray-700">
                Wohnsitz in Deutschland
              </label>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Abschnitt B – Unternehmensdaten</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gewünschter Firmenname *
                </label>
                <input
                  type="text"
                  name="company_name"
                  required
                  value={formData.company_name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Meine Firma GmbH"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alternativer Firmenname
                </label>
                <input
                  type="text"
                  name="alternative_company_name"
                  value={formData.alternative_company_name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Alternative Firma GmbH"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sitz der GmbH (Ort) *
              </label>
              <input
                type="text"
                name="company_location"
                required
                value={formData.company_location}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Berlin"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Geschäftszweck *
              </label>
              <textarea
                name="business_purpose"
                required
                value={formData.business_purpose}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder="Beschreibe deinen geplanten Geschäftszweck..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Anzahl Gesellschafter *
                </label>
                <input
                  type="number"
                  name="number_of_shareholders"
                  required
                  min="1"
                  value={formData.number_of_shareholders}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Geschäftsführer (Name/n) *
                </label>
                <input
                  type="text"
                  name="managing_directors"
                  required
                  value={formData.managing_directors}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Max Mustermann"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Abschnitt C – Gründung & Kapital</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Stammkapital *
              </label>
              <div className="space-y-2">
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    id="capital_own"
                    name="capital_type"
                    value="own"
                    checked={formData.capital_type === 'own'}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600"
                  />
                  <label htmlFor="capital_own" className="text-sm text-gray-700">
                    Eigenes Kapital
                  </label>
                </div>
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    id="capital_partner"
                    name="capital_type"
                    value="partner"
                    checked={formData.capital_type === 'partner'}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600"
                  />
                  <label htmlFor="capital_partner" className="text-sm text-gray-700">
                    Partnerlösung gewünscht
                  </label>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Höhe Stammkapital (€) *
              </label>
              <input
                type="number"
                name="capital_amount"
                required
                min="25000"
                step="1000"
                value={formData.capital_amount}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="25000"
              />
              <p className="text-xs text-gray-500 mt-1">Mindestens 25.000 € für eine GmbH</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Beteiligungsverhältnisse *
              </label>
              <input
                type="text"
                name="ownership_structure"
                required
                value={formData.ownership_structure}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="z.B. 100% oder 50-50 oder 60-30-10"
              />
              <p className="text-xs text-gray-500 mt-1">Gib die Verteilung der Anteile an (z.B. 100% bei alleinigem Gesellschafter oder 50-50 bei zwei gleichberechtigten Gesellschaftern)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Zeitpunkt der Gründung *
              </label>
              <div className="space-y-2">
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    id="timeline_immediate"
                    name="founding_timeline"
                    value="immediate"
                    checked={formData.founding_timeline === 'immediate'}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600"
                  />
                  <label htmlFor="timeline_immediate" className="text-sm text-gray-700">
                    Sofort
                  </label>
                </div>
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    id="timeline_1-2weeks"
                    name="founding_timeline"
                    value="1-2weeks"
                    checked={formData.founding_timeline === '1-2weeks'}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600"
                  />
                  <label htmlFor="timeline_1-2weeks" className="text-sm text-gray-700">
                    1–2 Wochen
                  </label>
                </div>
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    id="timeline_later"
                    name="founding_timeline"
                    value="later"
                    checked={formData.founding_timeline === 'later'}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600"
                  />
                  <label htmlFor="timeline_later" className="text-sm text-gray-700">
                    Später
                  </label>
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Abschnitt D – Bank & Organisation</h3>
            
            <div className="flex items-center space-x-3">
              <Checkbox
                id="existing_bank_contact"
                checked={formData.existing_bank_contact}
                onCheckedChange={(checked) => 
                  setFormData(prev => ({ ...prev, existing_bank_contact: checked }))
                }
              />
              <label htmlFor="existing_bank_contact" className="text-sm text-gray-700">
                Besteht bereits ein Bankkontakt?
              </label>
            </div>

            <div className="flex items-center space-x-3">
              <Checkbox
                id="assisted_account_opening"
                checked={formData.assisted_account_opening}
                onCheckedChange={(checked) => 
                  setFormData(prev => ({ ...prev, assisted_account_opening: checked }))
                }
              />
              <label htmlFor="assisted_account_opening" className="text-sm text-gray-700">
                Wunsch nach begleiteter Kontoeröffnung?
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bevorzugte Bank (falls vorhanden)
              </label>
              <input
                type="text"
                name="preferred_bank"
                value={formData.preferred_bank}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="z.B. Deutsche Bank, Commerzbank, N26..."
              />
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Abschnitt E – Online & Technik</h3>
            
            <div className="flex items-center space-x-3">
              <Checkbox
                id="needs_landing_page"
                checked={formData.needs_landing_page}
                onCheckedChange={(checked) => 
                  setFormData(prev => ({ ...prev, needs_landing_page: checked }))
                }
              />
              <label htmlFor="needs_landing_page" className="text-sm text-gray-700">
                Benötigte Landingpage
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Social Media
              </label>
              <div className="space-y-2">
                {['Instagram', 'Facebook', 'LinkedIn'].map(platform => (
                  <div key={platform} className="flex items-center space-x-3">
                    <Checkbox
                      id={`social_${platform}`}
                      checked={formData.social_media.includes(platform)}
                      onCheckedChange={() => handleArrayChange('social_media', platform)}
                    />
                    <label htmlFor={`social_${platform}`} className="text-sm text-gray-700">
                      {platform}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Ziel der KI-Bots
              </label>
              <div className="space-y-2">
                {['Termine', 'Anfragen', 'Support'].map(goal => (
                  <div key={goal} className="flex items-center space-x-3">
                    <Checkbox
                      id={`bot_${goal}`}
                      checked={formData.ai_bot_goals.includes(goal)}
                      onCheckedChange={() => handleArrayChange('ai_bot_goals', goal)}
                    />
                    <label htmlFor={`bot_${goal}`} className="text-sm text-gray-700">
                      {goal}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Abschnitt F – Bestätigung</h3>
            
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Wichtiger Hinweis</h4>
                  <p className="text-sm text-gray-700 leading-relaxed mb-4">
                    Ich bestätige, dass mir bekannt ist, dass keine Rechts- oder Steuerberatung erfolgt 
                    und notarielle sowie bankseitige Leistungen durch externe Partner erbracht werden.
                  </p>
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="legal_confirmation"
                      checked={formData.legal_confirmation}
                      onCheckedChange={(checked) => 
                        setFormData(prev => ({ ...prev, legal_confirmation: checked }))
                      }
                    />
                    <label htmlFor="legal_confirmation" className="text-sm font-medium text-gray-900">
                      Ich bestätige die oben genannten Hinweise *
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Zusammenfassung deiner Angaben</h4>
              <div className="space-y-2 text-sm">
                <p><span className="font-medium">Name:</span> {formData.full_name}</p>
                <p><span className="font-medium">Geburtsdatum:</span> {formData.date_of_birth || 'Nicht angegeben'}</p>
                <p><span className="font-medium">E-Mail:</span> {formData.email}</p>
                <p><span className="font-medium">Firmenname:</span> {formData.company_name}</p>
                <p><span className="font-medium">Standort:</span> {formData.company_location}</p>
                <p><span className="font-medium">Stammkapital:</span> {formData.capital_amount.toLocaleString('de-DE')} €</p>
                <p><span className="font-medium">Beteiligungsverhältnisse:</span> {formData.ownership_structure || 'Nicht angegeben'}</p>
                <p><span className="font-medium">Zeitpunkt:</span> {
                  formData.founding_timeline === 'immediate' ? 'Sofort' :
                  formData.founding_timeline === '1-2weeks' ? '1-2 Wochen' : 'Später'
                }</p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <PageSEO 
        title="GmbH-Käufer Erfassung | Firmbay"
        description="Erfassungsformular für GmbH-Käufer. Teilen Sie uns Ihre Anforderungen mit."
        path="/onboarding"
        robots="noindex, nofollow"
      />
      <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="pt-40 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            GmbH-Gründung
            <span className="block mt-2 pb-1 leading-tight bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
              Erfassungsformular
            </span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Teile uns alle wichtigen Informationen mit, damit wir dich optimal betreuen können
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Progress Indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isActive = step.number === currentStep;
                const isCompleted = step.number < currentStep;
                
                return (
                  <React.Fragment key={step.number}>
                    <div className="flex flex-col items-center">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all ${
                        isActive 
                          ? 'bg-gradient-to-r from-blue-600 to-teal-600 text-white shadow-lg' 
                          : isCompleted
                          ? 'bg-teal-500 text-white'
                          : 'bg-gray-200 text-gray-500'
                      }`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <span className={`text-xs font-medium hidden sm:block ${
                        isActive ? 'text-blue-600' : 'text-gray-500'
                      }`}>
                        {step.title}
                      </span>
                    </div>
                    {index < steps.length - 1 && (
                      <div className={`flex-1 h-1 mx-2 rounded transition-all ${
                        isCompleted ? 'bg-teal-500' : 'bg-gray-200'
                      }`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Form Card */}
          <Card className="border-2 border-gray-200 shadow-lg">
            <CardHeader>
              <CardTitle className="text-2xl">
                Schritt {currentStep} von 6
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={currentStep === 6 ? handleSubmit : (e) => e.preventDefault()}>
                {renderStepContent()}

                {/* Navigation Buttons */}
                <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBack}
                    disabled={currentStep === 1}
                    className="border-2 border-gray-300"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Zurück
                  </Button>

                  {currentStep < 6 ? (
                    <Button
                      type="button"
                      onClick={handleNext}
                      className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white"
                    >
                      Weiter
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      disabled={isSubmitting || !formData.legal_confirmation}
                      className="bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 text-white"
                    >
                      {isSubmitting ? 'Wird gesendet...' : 'Formular absenden'}
                      <CheckCircle className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
    </>
  );
};

export default OnboardingForm;
